import React from 'react'
import { withRouter } from 'react-router'
import { ComponentExt } from '@utils/reactExt'
import { Layout, Table, Tag, Tooltip, Button, Modal, Icon } from 'antd'
import moment from 'moment'
import NotificationModal from './NotificationModal'
import mediwhoAppService from '@services/mediwho-app'
import { ACTION_OPTIONS } from './notificationConstants'
import {
    mediwhoAppTableWrapStyle,
    centeredBodyCell,
    centeredHeaderCell,
    MediwhoAppPageHeader,
    renderTruncatedValue,
    renderActionButtons,
    rtlLayoutStyle,
    confirmDelete,
    normalizeTargets,
    TargetPopoverCell
} from '../shared/mediwhoAppStyles'

type TargetType = 'all' | 'hmo' | 'user_ids' | 'phones' | 'emails'

interface NotificationRow {
    id: number
    title: string
    body: string
    action?: string | null
    target_type: TargetType
    target_ids?: Array<string | number> | string | null
    is_successful?: boolean | number | null
    error?: string | null
    created_by?: string | null
    send_at?: string | null
}

interface RecipientRow {
    id: number
    first_name?: string
    last_name?: string
    phone?: string
    email?: string
}

interface State {
    data: NotificationRow[]
    total: number
    page: number
    loading: boolean
    modalVisible: boolean
    saving: boolean
    selected: NotificationRow | null
    resending: boolean
    recipientsModal: {
        visible: boolean
        loading: boolean
        notificationId: number | null
        targetType: string
        rows: RecipientRow[]
        total: number
        page: number
        lastPage: number
    }
}

const HMO_NAMES: Record<string, string> = {
    '1': 'כללית',
    '2': 'מכבי',
    '3': 'מאוחדת',
    '4': 'לאומית',
    '5': 'משרד הביטחון'
}

const HMO_COLORS: Record<string, string> = { '1': 'green', '2': 'geekblue', '3': 'orange', '4': 'purple', '5': 'cyan' }

const NOTIF_TARGET_META: Record<string, { label: string; color: string }> = {
    all: { label: 'כל המשתמשים', color: 'geekblue' },
    user_ids: { label: 'מזהי משתמש', color: 'purple' },
    phones: { label: 'טלפונים', color: 'cyan' },
    emails: { label: 'אימיילים', color: 'orange' }
}

function renderNotifTargetCell(row: NotificationRow, onShowAll?: () => void) {
    if (row.target_type === 'hmo') {
        const values = normalizeTargets(row.target_ids)
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                {values.map(value => (
                    <Tag key={String(value)} color={HMO_COLORS[String(value)] || 'default'}>
                        {HMO_NAMES[String(value)] || String(value)}
                    </Tag>
                ))}
            </div>
        )
    }
    return (
        <TargetPopoverCell
            targetType={row.target_type}
            targetValue={row.target_ids}
            targetMeta={NOTIF_TARGET_META}
            onShowAll={onShowAll}
        />
    )
}

function formatSendAt(value?: string | null) {
    if (!value) return 'נשלח מיד'
    const m = moment(value)
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : value
}

// a notification with no send_at was sent immediately on create; one with a past send_at was already sent
function isAlreadySent(row: NotificationRow) {
    if (!row.send_at) return true
    return moment(row.send_at).isBefore(moment())
}

class MediwhoNotificationsPage extends ComponentExt<any, State> {
    state: State = {
        data: [],
        total: 0,
        page: 1,
        loading: false,
        modalVisible: false,
        saving: false,
        selected: null,
        recipientsModal: {
            visible: false,
            loading: false,
            notificationId: null,
            targetType: '',
            rows: [],
            total: 0,
            page: 1,
            lastPage: 1
        }
    }

    componentDidMount() {
        this.load(1)
    }

    load = async (page: number) => {
        this.setState({ loading: true })
        try {
            const res = await mediwhoAppService.listNotifications({ page, perPage: 10 })
            const payload = res.data || {}
            this.setState({
                data: payload.data || [],
                total: payload.total || 0,
                page: payload.page || page
            })
        } catch (error) {
            console.error(error)
            this.$message.error('לא ניתן לטעון את רשימת ההתראות')
        } finally {
            this.setState({ loading: false })
        }
    }

    openModal = (row?: NotificationRow) => {
        this.setState({ modalVisible: true, selected: row || null, resending: false })
    }

    closeModal = () => {
        this.setState({ modalVisible: false, selected: null, resending: false })
    }

    handleSave = async (payload: Record<string, any>) => {
        const { selected, resending } = this.state
        this.setState({ saving: true })
        try {
            if (selected && selected.id && !resending) {
                await mediwhoAppService.updateNotification(selected.id, payload)
                this.$message.success('ההתראה עודכנה')
            } else {
                const res = await mediwhoAppService.createNotification(payload)
                const responseBody = res.data || {}
                if (responseBody.status === 'no_recipients'|| responseBody.status === 'no_recipients_with_FCM_token') {
                    this.$message.warning('ההתראה נשמרה, אבל לא נמצאו נמענים עם FCM token')
                } else if (responseBody.status === 'failed') {
                    const notif = responseBody.notification || {}
                    this.$message.error('שליחת ההתראה נכשלה: ' + (notif.error || 'שגיאה לא ידועה'))
                } else {
                    this.$message.success('ההתראה נוצרה ונשלחה')
                }
            }
            this.closeModal()
            await this.load(1)
        } catch (error) {
            const raw = error && error.response && error.response.data
            const errorMessage = typeof raw === 'string' ? raw : (error && error.message) || 'שגיאה בשמירת ההתראה'
            this.$message.error(errorMessage)
        } finally {
            this.setState({ saving: false })
        }
    }

    deleteNotification = (id: number) => {
        confirmDelete('מחיקת התראה', 'האם אתה בטוח שברצונך למחוק את ההתראה?', async () => {
            try {
                await mediwhoAppService.deleteNotification(id)
                this.$message.success('ההתראה נמחקה')
                this.load(this.state.page)
            } catch (error) {
                this.$message.error('שגיאה במחיקת ההתראה')
            }
        })
    }

    openRecipients = async (row: NotificationRow, page: number = 1) => {
        this.setState(prev => ({
            recipientsModal: {
                ...prev.recipientsModal,
                visible: true,
                loading: true,
                notificationId: row.id,
                targetType: '',
                rows: [],
                total: 0,
                page,
                lastPage: 1
            }
        }))
        try {
            const res = await mediwhoAppService.getNotificationRecipients(row.id, page)
            const d = res.data
            this.setState(prev => ({
                recipientsModal: {
                    ...prev.recipientsModal,
                    loading: false,
                    targetType: d.target_type,
                    rows: d.data,
                    total: d.total,
                    page: d.page,
                    lastPage: d.lastPage
                }
            }))
        } catch (error) {
            this.$message.error('שגיאה בטעינת מקבלי ההתראה')
            this.setState(prev => ({ recipientsModal: { ...prev.recipientsModal, loading: false } }))
        }
    }

    closeRecipients = () => {
        this.setState(prev => ({ recipientsModal: { ...prev.recipientsModal, visible: false } }))
    }

    resendNotification = (row: NotificationRow) => {
        this.setState({
            modalVisible: true,
            selected: { ...row, id: undefined as any, send_at: null },
            resending: true
        })
    }

    columns = () => [
        {
            title: 'כותרת',
            dataIndex: 'title',
            key: 'title',
            width: '18%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (value: string, row: NotificationRow) => {
                const failed = row.is_successful === false || row.is_successful === 0
                const cell = (
                    <div
                        style={{
                            fontWeight: failed ? 700 : 400,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                        className="mediwho-notification-title-cell"
                    >
                        {failed && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    background: '#ff4d4d',
                                    color: 'white',
                                    borderRadius: '50%',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    flexShrink: 0
                                }}
                            >
                                !
                            </span>
                        )}
                        <div style={{ flex: 1, textAlign: 'center', marginRight: failed ? '-24px' : 0 }}>{value}</div>
                    </div>
                )
                // whole title cell shows the error tooltip on hover when failed
                return failed ? <Tooltip title={row.error || 'שליחת ההתראה נכשלה'}>{cell}</Tooltip> : cell
            }
        },
        {
            title: 'תוכן',
            dataIndex: 'body',
            key: 'body',
            width: '20%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (value: string) => renderTruncatedValue(value, 60)
        },
        {
            title: 'יעד',
            key: 'target_type',
            width: '10%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (_: any, row: NotificationRow) => renderNotifTargetCell(row, () => this.openRecipients(row))
        },
        {
            title: 'תאריך שליחה',
            dataIndex: 'send_at',
            key: 'send_at',
            width: '16%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (value: string | null) => formatSendAt(value)
        },
        {
            title: (
                <Tooltip title="שם המשתמש שיצר את ההתראה">
                    <span>נוצר על ידי</span>
                </Tooltip>
            ),
            dataIndex: 'created_by',
            key: 'created_by',
            width: '10%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (value: string | null) => value || '—'
        },
        {
            title: (
                <Tooltip title="הניווט שנפתח באפליקציה לאחר לחיצה על ההתראה">
                    <span>פעולה</span>
                </Tooltip>
            ),
            dataIndex: 'action',
            key: 'action',
            width: '12%',
            onHeaderCell: centeredHeaderCell,
            onCell: centeredBodyCell,
            render: (value: string | null) => {
                const found = value && ACTION_OPTIONS.find(o => o.value === value)
                return found ? <Tag color="purple">{found.label}</Tag> : '—'
            }
        },
        {
            title: '',
            key: 'buttons',
            width: '12%',
            onCell: () => ({ style: { verticalAlign: 'middle' as const } }),
            render: (_: any, row: NotificationRow) => {
                const sent = isAlreadySent(row)
                return (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        {renderActionButtons({
                            onEdit: () => this.openModal(row),
                            onDelete: () => this.deleteNotification(row.id),
                            disabled: sent,
                            onResend: sent ? () => this.resendNotification(row) : undefined
                        })}
                    </div>
                )
            }
        }
    ]

    render() {
        const { data, total, page, loading, modalVisible, saving, selected, resending, recipientsModal } = this.state
        const pendingCount = data.filter((r: NotificationRow) => !isAlreadySent(r)).length
        const subtitle =
            data.length === 0
                ? ''
                : pendingCount === 1
                ? 'התראה אחת ממתינה לשליחה'
                : pendingCount + ' התראות ממתינות לשליחה'

        return (
            <Layout style={rtlLayoutStyle} className="mediwho-notifications-page">
                <MediwhoAppPageHeader
                    title="התראות +60"
                    subtitle={subtitle}
                    actionLabel="+ התראה חדשה"
                    onAction={() => this.openModal()}
                />
                <div style={mediwhoAppTableWrapStyle} className="mediwho-app-table-wrap">
                    <Table
                        rowKey="id"
                        loading={loading}
                        dataSource={data}
                        columns={this.columns()}
                        tableLayout="fixed"
                        rowClassName={(row: NotificationRow) => {
                            const failed = row.is_successful === false || row.is_successful === 0
                            if (failed) {
                                return 'mediwho-app-row-failed'
                            }
                            return isAlreadySent(row) ? 'mediwho-app-row-past' : ''
                        }}
                        pagination={{
                            current: page,
                            total,
                            pageSize: 10,
                            showSizeChanger: false,
                            onChange: (nextPage: number) => this.load(nextPage)
                        }}
                    />
                </div>

                <NotificationModal
                    visible={modalVisible}
                    saving={saving}
                    notification={selected}
                    resending={resending}
                    onCancel={this.closeModal}
                    onSubmit={this.handleSave}
                />
                <Modal
                    visible={recipientsModal.visible}
                    title={`מקבלי ההתראה (${recipientsModal.total.toLocaleString()})`}
                    footer={null}
                    onCancel={this.closeRecipients}
                    width={700}
                >
                    <Table
                        loading={recipientsModal.loading}
                        dataSource={recipientsModal.rows}
                        rowKey={r => String(r.id)}
                        tableLayout="fixed"
                        pagination={{
                            current: recipientsModal.page,
                            total: recipientsModal.total,
                            pageSize: 20,
                            onChange: p => {
                                const row = this.state.data.find(d => d.id === recipientsModal.notificationId)
                                if (row) this.openRecipients(row, p)
                            }
                        }}
                        columns={[
                            {
                                title: 'מזהה משתמש',
                                dataIndex: 'id',
                                width: 120,
                                onHeaderCell: centeredHeaderCell,
                                onCell: centeredBodyCell
                            },
                            {
                                title: 'שם פרטי',
                                dataIndex: 'first_name',
                                onHeaderCell: centeredHeaderCell,
                                onCell: centeredBodyCell
                            },
                            {
                                title: 'שם משפחה',
                                dataIndex: 'last_name',
                                onHeaderCell: centeredHeaderCell,
                                onCell: centeredBodyCell
                            },
                            {
                                title: 'טלפון',
                                dataIndex: 'phone',
                                onHeaderCell: centeredHeaderCell,
                                onCell: centeredBodyCell
                            },
                            {
                                title: 'אימייל',
                                dataIndex: 'email',
                                onHeaderCell: centeredHeaderCell,
                                onCell: centeredBodyCell
                            }
                        ]}
                    />
                </Modal>
            </Layout>
        )
    }
}

export default withRouter(MediwhoNotificationsPage)
